import { useLogin } from "@/components/providers/ApiProvider/hooks";
import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Form from "@radix-ui/react-form";
import {
  Button,
  Flex,
  Heading,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import PasswordInputToggleField from "@/components/ui/form/PasswordInputToggleField";

const passwordLoginSchema = z.object({
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters." }),
});

type PasswordLoginFormValues = z.infer<typeof passwordLoginSchema>;

type PasswordLoginFormProps = {
  email: string;
  onSuccess: () => void;
  onForgotPassword: () => void;
  onCodeInstead: () => void;
  onUseDifferentEmail: () => void;
  compact?: boolean;
};

export function PasswordLoginForm({
  email,
  onSuccess,
  onForgotPassword,
  onCodeInstead,
  onUseDifferentEmail,
  compact,
}: PasswordLoginFormProps) {
  const [loading, setLoading] = useState(false);
  const { handleSubmit, control } = useForm<PasswordLoginFormValues>({
    resolver: zodResolver(passwordLoginSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { password: "" },
  });
  const loginUser = useLogin();

  const onSubmit = async (data: PasswordLoginFormValues) => {
    setLoading(true);
    try {
      await loginUser(email, data.password, true);
      onSuccess();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
      setLoading(false);
    }
  };

  return (
    <Flex
      className={`w-full ${compact ? "gap-4" : "gap-6 sm:gap-8"}`}
      direction="column"
      aria-labelledby="password-login-form-title"
    >
      <div className="space-y-2 text-center">
        <Heading
          id="password-login-form-title"
          as="h2"
          weight="bold"
          className={`font-figtree text-blue-900 leading-[1.1] ${compact ? "text-[28px] tracking-[-0.28px]" : "text-[32px] tracking-[-0.32px] sm:text-[40px] sm:tracking-[-0.4px]"}`}
        >
          Sign in with password
        </Heading>
      </div>

      <Form.Root
        className={`flex flex-col ${compact ? "gap-3" : "gap-4 sm:gap-6"}`}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Text size="2" className="text-gray-9">
              Email
            </Text>
            <TextField.Root
              value={email}
              disabled
              name="email"
              autoComplete="username"
              className="cursor-default [&_input]:cursor-default"
            />
          </div>

          <div className="space-y-1.5">
            <Text size="2" className="text-gray-9">
              Password
            </Text>
            <PasswordInputToggleField
              control={control}
              name="password"
              label="Password"
              required
              autoComplete="current-password"
              data-testid="auth-login-password-input"
            />
          </div>
        </div>

        <Form.Submit asChild>
          <Button
            loading={loading}
            type="submit"
            radius="full"
            className="h-[40px]"
            data-testid="auth-login-submit-button"
          >
            Sign in
          </Button>
        </Form.Submit>

        <div className="text-center">
          <button
            type="button"
            onClick={onCodeInstead}
            className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
            data-testid="auth-login-code-link"
          >
            Continue with code instead
          </button>
        </div>

        <Separator className="w-full bg-gray-6" />

        <Flex justify="between" align="center">
          <button
            type="button"
            onClick={onUseDifferentEmail}
            className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
          >
            &larr; Use a different email
          </button>
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
          >
            Forgot password?
          </button>
        </Flex>
      </Form.Root>
    </Flex>
  );
}

export default PasswordLoginForm;
